import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { getUserInitials } from '../../utils/userInitials';
import {
  adminDashboardTabHref,
  adminDashboardTabItems,
  isAdminDashboardArea,
  parseAdminDashboardTab,
} from '../../utils/adminDashboardTabs';
import {
  insurerDashboardTabHref,
  insurerDashboardTabItems,
  isInsurerDashboardArea,
  parseInsurerDashboardTab,
} from '../../utils/insurerDashboardTabs';



export interface DesktopNavigationProps {
  isActive: (path: string) => boolean;
  handleFeatureClick: (feature: string) => void;
  isUserDropdownOpen: boolean;
  setIsUserDropdownOpen: (open: boolean) => void;
  userDropdownRef: React.RefObject<HTMLDivElement>;
  handleLogout: () => void;
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  isActive,
  handleFeatureClick,
  isUserDropdownOpen,
  setIsUserDropdownOpen,
  userDropdownRef,
  handleLogout,
}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const { subscription } = useSubscriptionContext();
  const isAdmin = user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN');
  const isInsurer = user && user.role === 'INSURER';
  const isOrgAdmin = user?.org_role === 'org_admin';
  const isPartnerClient = Boolean(user?.insurer_org_id && user?.role === 'USER');
  const onAdminArea = isAdminDashboardArea(location.pathname);
  const onInsurerArea = Boolean(isInsurer) && isInsurerDashboardArea(location.pathname);
  const hideConsumerNav = isInsurer || isPartnerClient || (Boolean(isAdmin) && onAdminArea);
  const showAdminTabs = Boolean(isAdmin) && onAdminArea;
  const showInsurerTabs = Boolean(isInsurer);
  const [searchParams] = useSearchParams();
  const activeAdminTab = parseAdminDashboardTab(searchParams);
  const activeInsurerTab = onInsurerArea
    ? parseInsurerDashboardTab(
        searchParams,
        Boolean(isOrgAdmin),
        location.pathname
      )
    : null;
  const navScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = navScrollRef.current;
    if (!root) return;
    const revealActive = () => {
      const active = root.querySelector<HTMLElement>('[data-nav-active="true"]');
      if (!active) return;
      const rootRect = root.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      if (activeRect.left < rootRect.left + 4) {
        root.scrollLeft -= rootRect.left - activeRect.left + 8;
      } else if (activeRect.right > rootRect.right - 4) {
        root.scrollLeft += activeRect.right - rootRect.right + 8;
      }
    };
    revealActive();
    window.addEventListener('resize', revealActive);
    return () => window.removeEventListener('resize', revealActive);
  }, [location.pathname, showAdminTabs, showInsurerTabs]);
  const itemClass = (active: boolean) =>
    `whitespace-nowrap shrink-0 px-2 py-2 xl:px-3 text-sm font-medium rounded-md transition-colors min-h-[44px] flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
      active
        ? 'text-blue-600 bg-blue-50'
        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
    }`;

  if (!isAuthenticated) {
    return (
      <div className="hidden items-center space-x-4 md:flex">
        <Link
          to="/login"
          className={`px-3 py-2 text-sm font-medium rounded-md ${ isActive('/login')
              ? 'text-blue-600 bg-blue-50'
              : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          Sign In
        </Link>
        <Link
          to="/register"
          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Get Started
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
      <div
        ref={navScrollRef}
        className="flex min-w-0 flex-1 items-center overflow-x-auto [scrollbar-width:thin]"
      >
        <div className="flex items-center gap-0.5 ml-auto">
        {/* Admin dashboard tabs - shown in place of consumer nav while on /admin */}
        {showAdminTabs &&
          adminDashboardTabItems().map(({ key, label }) => (
            <Link
              key={key}
              to={adminDashboardTabHref(key)}
              data-nav-active={activeAdminTab === key ? 'true' : undefined}
              className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px] flex items-center shrink-0 ${
                activeAdminTab === key
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              {label}
            </Link>
          ))}

        {showInsurerTabs &&
          insurerDashboardTabItems(Boolean(isOrgAdmin)).map(({ key, label }) => {
            const isTabActive = activeInsurerTab === key;
            return (
              <Link
                key={key}
                to={insurerDashboardTabHref(key)}
                data-nav-active={isTabActive ? 'true' : undefined}
                className={`whitespace-nowrap px-4 py-1.5 text-sm font-semibold rounded-lg transition-all flex items-center shrink-0 ${
                  isTabActive
                    ? 'bg-slate-100 text-[#0c1e40] border-b-2 border-[#991b1b] shadow-sm'
                    : 'text-gray-600 hover:text-[#0c1e40] hover:bg-gray-50'
                }`}
              >
                {label}
              </Link>
            );
          })}


        {/* Admin Navigation outside admin area */}
        {isAdmin && !isInsurer && !onAdminArea && (
          <>
            <Link
              to="/admin"
              data-nav-active={isActive('/admin') ? 'true' : undefined}
              className={itemClass(isActive('/admin'))}
            >
              Admin
            </Link>
            <Link
              to="/analytics-dashboard"
              data-nav-active={isActive('/analytics-dashboard') ? 'true' : undefined}
              className={itemClass(isActive('/analytics-dashboard'))}
            >
              Analytics
            </Link>
            <Link
              to="/dashboard"
              data-nav-active={isActive('/dashboard') ? 'true' : undefined}
              className={itemClass(isActive('/dashboard'))}
            >
              Workspace
            </Link>
          </>
        )}

        {/* Exercises and Continuity plan — only these are visible for normal users */}
        {!isInsurer && (!isAdmin || !onAdminArea) && (
          <>
            <Link
              to="/tabletop-exercises"
              data-nav-active={isActive('/tabletop-exercises') ? 'true' : undefined}
              className={itemClass(isActive('/tabletop-exercises'))}
            >
              Exercises
            </Link>
            <Link
              to="/bcp"
              data-nav-active={isActive('/bcp') ? 'true' : undefined}
              className={itemClass(isActive('/bcp'))}
            >
              Continuity plan
            </Link>
            <Link
              to="/academy"
              data-nav-active={isActive('/academy') ? 'true' : undefined}
              className={itemClass(isActive('/academy'))}
            >
              Academy
            </Link>
          </>
        )}
        </div>
      </div>

      {/* User Menu */}
      <div className="relative shrink-0" ref={userDropdownRef}>
        {isInsurer ? (
          <button
            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            className={`w-9 h-9 rounded-full bg-[#0c1e40] text-white font-bold text-xs tracking-wider flex items-center justify-center transition-all focus:outline-none ${
              isActive('/profile') ? 'ring-2 ring-[#991b1b] ring-offset-2' : 'hover:opacity-90'
            }`}
            aria-label={user?.full_name || user?.email || 'User'}
            title={user?.full_name || user?.email || 'User'}
          >
            {getUserInitials(user?.full_name, user?.email)}
          </button>
        ) : (
          <button
            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            aria-label={user?.full_name || user?.email || 'User'}
            className="flex items-center px-3 py-2 space-x-2 text-sm font-medium text-gray-700 rounded-md hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <div className="flex items-center space-x-2">
              <div
                className="flex justify-center items-center w-9 h-9 bg-gray-200 rounded-full shrink-0"
                aria-label={user?.full_name || user?.email || 'User'}
                title={user?.full_name || user?.email || 'User'}
              >
                <span className="text-sm font-medium text-gray-700">
                  {getUserInitials(user?.full_name, user?.email)}
                </span>
              </div>
              {subscription && (
                <div className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                  {subscription.plan_type.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}

        {/* User Dropdown Menu */}
        {isUserDropdownOpen && (
          <div className="absolute right-0 top-full z-50 py-1 mt-2 w-56 bg-white rounded-md border border-gray-200 shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-900">
                {user?.full_name || 'User'}
              </div>
              <div className="text-sm text-gray-500">
                {user?.email || 'user@example.com'}
              </div>
            </div>

            <Link
              to="/profile"
              onClick={() => setIsUserDropdownOpen(false)}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${ isActive('/profile') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              Profile
            </Link>

            {!hideConsumerNav && (
            <>
            <Link
              to="/subscription-management"
              onClick={() => setIsUserDropdownOpen(false)}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${ isActive('/subscription-management') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              Subscriptions
            </Link>

            <Link
              to="/payment-history"
              onClick={() => setIsUserDropdownOpen(false)}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${ isActive('/payment-history') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              Payments
            </Link>

            <Link
              to="/billing-settings"
              onClick={() => setIsUserDropdownOpen(false)}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${ isActive('/billing-settings') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              Billing
            </Link>
            </>
            )}

            {isAdmin && (
              <>
                <div className="my-1 border-t border-gray-200"></div>
                <Link
                  to="/admin"
                  onClick={() => setIsUserDropdownOpen(false)}
                  className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${ isActive('/admin') ? 'text-blue-600 bg-blue-50' : ''}`}
                >
                  Admin
                </Link>
                {onAdminArea && (
                  <Link
                    to="/tabletop-exercises"
                    onClick={() => setIsUserDropdownOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Back to App
                  </Link>
                )}
              </>
            )}

            <div className="my-1 border-t border-gray-200"></div>

            <button
              onClick={handleLogout}
              className="block px-4 py-2 w-full text-sm text-left text-red-600 hover:bg-red-50"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopNavigation;
